/**
 * Synthesise a wire-shape `Invoice` from the in-progress invoice form
 * state so the artboard can render a live preview *without* the user
 * having to save the draft first.
 *
 * Mirrors `apps/mobile/.../buildPreviewInvoice.ts` so web + mobile
 * previews render the same shape. Output is a "ghost invoice" —
 * synthetic ids, never round-trips through the server. Do NOT feed
 * it to a create / update mutation; use `buildCreateInvoiceRequest`
 * for that path.
 */
import type {
  Client,
  Invoice,
  InvoiceLineItem,
} from '@repo/api-client/dtos';

import {
  formTaxToWire,
  formDiscountToWire,
  type InvoiceFormValues,
} from './invoiceFormSchema';
import { parseUserMoney } from '@/features/money';

export const PREVIEW_INVOICE_ID = '00000000-0000-4000-8000-000000000000' as const;

/** Ghost invoices from {@link buildPreviewInvoice} must not be PATCHed. */
export function isPreviewInvoiceId(id: string | undefined | null): boolean {
  return !id || id === PREVIEW_INVOICE_ID;
}
const PREVIEW_ORG_ID = '00000000-0000-4000-8000-000000000001' as const;

export interface BuildPreviewInvoiceArgs {
  values: InvoiceFormValues;
  /** Totals already computed by `useLiveTotals` — minor-unit strings. */
  totals: {
    subtotal: string;
    discountTotal: string;
    taxTotal: string;
    total: string;
  };
  /**
   * Resolved minor-unit unit prices per line, in the same order as
   * `values.lineItems`. Caller usually has these from the live-totals
   * pass; if not, pass an empty array and we'll re-parse.
   */
  lineUnitPriceMinor?: readonly string[];
  /** Server-issued invoice number (only present on edit). */
  invoiceNumber?: string;
  /** Selected client (`null` while picking — falls back to the form's clientId). */
  client: Client | null;
}

export function buildPreviewInvoice(args: BuildPreviewInvoiceArgs): Invoice {
  const { values, totals, lineUnitPriceMinor, invoiceNumber, client } = args;
  const currency = values.currency;
  const taxConfig = formTaxToWire(values.tax);
  const invoiceDiscount = formDiscountToWire(values.invoiceDiscount, currency);
  const lineItems: InvoiceLineItem[] = values.lineItems.map((line, idx) =>
    toPreviewLineItem(line, idx, currency, lineUnitPriceMinor?.[idx]),
  );

  const clientName =
    client?.displayName ||
    client?.name ||
    values.clientId ||
    'Draft client';

  const nowIso = new Date().toISOString();

  return {
    id: PREVIEW_INVOICE_ID,
    orgId: PREVIEW_ORG_ID,
    clientId: client?.id ?? values.clientId ?? PREVIEW_ORG_ID,
    clientNameSnapshot: clientName,
    invoiceNumber: invoiceNumber ?? 'DRAFT',
    status: 'draft',
    currency,
    issueDate: toIso(values.issueDate),
    dueDate: toIso(values.dueDate),
    taxConfig,
    invoiceDiscount: invoiceDiscount ?? null,
    paymentMethods: values.paymentMethods ?? [],
    totals: {
      subtotal: totals.subtotal,
      discountTotal: totals.discountTotal,
      taxTotal: totals.taxTotal,
      total: totals.total,
      amountPaid: '0',
      amountDue: totals.total,
    },
    notes: values.notes && values.notes.length > 0 ? values.notes : null,
    terms: values.terms && values.terms.length > 0 ? values.terms : null,
    signature:
      values.signature && values.signature.length > 0 ? values.signature : null,
    templateId: values.templateId,
    createdAt: nowIso,
    updatedAt: nowIso,
    lineItems,
  };
}

function toPreviewLineItem(
  line: InvoiceFormValues['lineItems'][number],
  index: number,
  currency: string,
  prefetchedMinor: string | undefined,
): InvoiceLineItem {
  const unitPrice = (() => {
    if (prefetchedMinor !== undefined) return prefetchedMinor;
    const parsed = parseUserMoney(line.unitPriceInput, currency);
    return parsed.ok ? parsed.value.amount.toString() : '0';
  })();
  // The stored InvoiceLineItem shape splits `discount` into a value +
  // separate `discountType` (legacy column layout) rather than the
  // discriminated union the create wire uses. Translate back here.
  const discountColumns = (() => {
    if (!line.discount) return {};
    if (line.discount.type === 'percentage') {
      return {
        discount: line.discount.rate,
        discountType: 'percentage' as const,
      };
    }
    const parsed = parseUserMoney(line.discount.amount, currency);
    if (!parsed.ok) return {};
    return {
      discount: parsed.value.amount.toString(),
      discountType: 'fixed' as const,
    };
  })();
  return {
    id: line.id ?? `preview-line-${index}`,
    invoiceId: PREVIEW_INVOICE_ID,
    position: index,
    description: line.description || 'Line item',
    quantity: line.quantity || '1',
    unitPrice,
    unit: line.unit,
    taxable: line.taxable,
    ...(line.taxRate && line.taxRate.trim() !== ''
      ? { taxRate: line.taxRate }
      : {}),
    ...discountColumns,
    notes: line.notes && line.notes.length > 0 ? line.notes : null,
  };
}

function toIso(yyyyMmDd: string): string {
  if (!yyyyMmDd) return new Date().toISOString();
  const d = new Date(yyyyMmDd);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}
